import React, { Component } from "react";
import { Accessor, GState, ZTime } from "IZOX/STATIC";
import PropsType from "prop-types";
import _ from "lodash";
import { HStack, VStack, XButton, XText } from "IZOX/_X";
import moment from "moment";

/**
 * @augments {Component<Props, State>}
 */
class DateCore extends Component {

  static propTypes = {
    disMonth: PropsType.number,
    disYear: PropsType.number,

    dayOfWeekBar: PropsType.bool,
    weekendHighlight: PropsType.bool,

    _isValid: PropsType.func,
    _onDateSelect: PropsType.func,
    _onSelectorChange: PropsType.func,

    selectedDate: PropsType.string,
    selectedDates: PropsType.array,
    selectedStart: PropsType.string,
    selectedEnd: PropsType.string,
    styles: PropsType.object,
    dayCellDecorator: PropsType.func
  };

  static defaultProps = {
    disYear: ZTime.Now().year(),
    disMonth: ZTime.Now().month(),

    dayOfWeekBar: true,
    weekendHighlight: true,

    _isValid: null,
    _onDateSelect: null,
    _onSelectorChange: null,

    selectedDate: "",
    selectedDates: [],
    selectedStart: "",
    selectedEnd: "",
    styles: {},
    dayCellDecorator: null
  };

  constructor(props){
    super();
    let {disMonth, disYear} = props;
    let refDate = moment(`${String(disMonth + 1).padStart(2, "0")}${disYear}`, "MMYYYY");

    let startOfMonth = ZTime.StartOfMonth(refDate, null);
    let dayOfWeek = startOfMonth.day();
    let startDate = ZTime.Moment(startOfMonth).add(-dayOfWeek, "days");
    
    this.state = {
      startMoment: startDate.toISOString()
    };
  }

  componentDidMount(){
    this._setAllStates();
  }

  componentDidUpdate(prevProps, prevState){
    if(!Accessor.IsIdentical(prevProps, this.props, Object.keys(DateCore.defaultProps))){
      this._setAllStates(() => {
        let {disMonth, disYear} = this.props;
        let startOfMonth = ZTime.StartOfMonth(new Date(disYear, disMonth), null);
        let dayOfWeek = startOfMonth.day();
        let startDate = ZTime.Moment(startOfMonth).add(-dayOfWeek, "days");
        this.setState({
          startMoment: startDate.toISOString()
        });
      });
    }
  }

  componentWillUnmount() {
    this.setState = (state, callback) => {
      return;
    };
  }

  _setAllStates = (callback) => {
    this.setState((state, props) => ({
      ...props,
    }), callback);
  };

  toLastMonth = () => {
    let {disMonth, disYear, _onSelectorChange} = this.props;
    if(disMonth === 0) { 
      disMonth = 11;
      disYear -= 1;
    }else{
      disMonth -= 1;
    }
    _onSelectorChange?.(disYear, disMonth);
  };

  toNextMonth = () => {
    let {disMonth, disYear, _onSelectorChange} = this.props;
    if(disMonth === 11) { 
      disMonth = 0;
      disYear += 1;
    }else{
      disMonth += 1;
    }
    _onSelectorChange?.(disYear, disMonth);
  };

  renderCell(week, dayOfWeek, key){
    let {_isValid, _onDateSelect, disMonth, selectedDates, multiple,
      selectedDate, selectedStart, selectedEnd, range, selectType, size, styles, dayCellDecorator} = this.props;
    let {startMoment} = this.state;
    let thisDate = ZTime.Moment(startMoment).add(week * 7 + dayOfWeek, "days");
    let str = thisDate.date();

    let isToday = ZTime.IsBetween(ZTime.Now().toISOString(), 
      ZTime.StartOfDay(thisDate, null).toISOString(), 
      ZTime.EndOfDay(thisDate, null).toISOString(), "[]");

    let cellStyle = styles.cell(size);
    if(dayOfWeek === 0){
      cellStyle = {...cellStyle, ...styles.cellSun};
    }
    if(dayOfWeek === 6){
      cellStyle = {...cellStyle, ...styles.cellSat};
    }

    let textStyle = styles.cellText(size);
    if(dayOfWeek === 0){
      textStyle = {...textStyle, ...styles.cellTextSun};
    }
    if(dayOfWeek === 6){
      textStyle = {...textStyle, ...styles.cellTextSat};
    }

    if(isToday){
      textStyle = {...textStyle, ...styles.cellTextToday(size)};
    }

    if(thisDate.month() !== disMonth){
      textStyle = {...textStyle, ...styles.cellTextNotInMonth};
    }

    let isValid = _isValid(thisDate.year(), thisDate.month(), thisDate.date());
    if(!isValid){
      textStyle = {...textStyle, ...styles.cellTextNotValid};
    }

    let highlightStyle = styles.highlightStyle;
    if(range || selectType !== "date"){
      if(ZTime.StartOfDay(selectedStart, null).isSame(ZTime.StartOfDay(thisDate, null))){
        if(ZTime.StartOfDay(selectedEnd, null).isSame(ZTime.StartOfDay(thisDate, null))){
          highlightStyle = {...highlightStyle, ...styles.highlightStyleDate(size)};
          textStyle = {...textStyle, ...styles.highlightStyleText};
        }else{
          highlightStyle = {...highlightStyle, ...styles.highlightStyleStart(size)};
          textStyle = {...textStyle, ...styles.highlightStyleText};
        }
      }else if(ZTime.StartOfDay(selectedEnd, null).isSame(ZTime.StartOfDay(thisDate, null))){
        highlightStyle = {...highlightStyle, ...styles.highlightStyleEnd(size)};
        textStyle = {...textStyle, ...styles.highlightStyleText};
      }else if(ZTime.IsBetween(thisDate, ZTime.StartOfDay(selectedStart, null), ZTime.StartOfDay(selectedEnd, null), "[]")){
        highlightStyle = {...highlightStyle, ...styles.highlightStyleIn(size)};
        textStyle = {...textStyle, ...styles.highlightStyleText};
      }
    }else if(multiple){
      if(_.some(selectedDates, o => ZTime.StartOfDay(o, null).isSame(ZTime.StartOfDay(thisDate, null)))){
        highlightStyle = {...highlightStyle, ...styles.highlightStyleDate(size)};
        textStyle = {...textStyle, ...styles.highlightStyleText};
      }
    }else{
      if(ZTime.StartOfDay(selectedDate, null).isSame(ZTime.StartOfDay(thisDate, null))){
        highlightStyle = {...highlightStyle, ...styles.highlightStyleDate(size)};
        textStyle = {...textStyle, ...styles.highlightStyleText};
      }
    }

    return (
      <XButton key={key} style={cellStyle} padding={0}
        onClick={() => _onDateSelect(thisDate)} disabled={!isValid}>
        {dayCellDecorator &&
          <VStack position="absolute" top={0} fullHeight fullWidth>
            {dayCellDecorator?.(thisDate.toISOString(), {
              isInRange: ZTime.IsBetween(thisDate, ZTime.StartOfDay(selectedStart, null), ZTime.StartOfDay(selectedEnd, null), "[]"),
              isStart: ZTime.StartOfDay(selectedStart, null).isSame(ZTime.StartOfDay(thisDate, null)),
              isEnd: ZTime.StartOfDay(selectedEnd, null).isSame(ZTime.StartOfDay(thisDate, null)),
              isSelected: multiple ? _.some(selectedDates, o => ZTime.StartOfDay(o, null).isSame(ZTime.StartOfDay(thisDate, null))) : ZTime.StartOfDay(selectedDate, null).isSame(ZTime.StartOfDay(thisDate, null)),
              isToday,
              isValid
            })}
          </VStack>}
        <VStack style={highlightStyle} HAlign="|" VAlign="-">
          <XText style={textStyle}>
            {str}
          </XText>
        </VStack>
      </XButton>
    );
  }

  renderWeek(week, key){
    let {styles} = this.props;
    let {startMoment} = this.state;
    let isThisWeek = ZTime.IsBetween(ZTime.Now(), 
      ZTime.Moment(startMoment).add(week * 7, "days"), 
      ZTime.Moment(startMoment).add(week * 7 + 7, "days"), "[)");

    return (
      <HStack key={key} {...(isThisWeek ? styles.thisWeekBar : styles.notThisWeekBar)}>
        {_.map(_.range(0, 7), (o, i) => {
          return this.renderCell(week, o, i);
        })}
      </HStack>
    );
  }

  renderMain(){
    return (
      // <SwipeableView onSwipeLeft={this.toNextMonth} onSwipeRight={this.toLastMonth}>
        <VStack fullWidth>
          {_.map(_.range(0, 6), (o, i) => {
            return this.renderWeek(o, i);
          })}
        </VStack>
      // </SwipeableView>
    );
  }

  renderWeekBarCell(dayOfWeek, key){
    let {size, styles} = this.props;

    let str = ZTime.dayOfWeekDis?.[GState.lang]?.OneLetter?.[dayOfWeek];

    let cellStyle = styles.cellWeek(size);
    if(dayOfWeek === 0){
      cellStyle = {...cellStyle, ...styles.cellSun};
    }
    if(dayOfWeek === 6){
      cellStyle = {...cellStyle, ...styles.cellSat};
    }

    let textStyle = styles.cellText(size);
    if(dayOfWeek === 0){
      textStyle = {...textStyle, ...styles.cellTextSun};
    }
    if(dayOfWeek === 6){
      textStyle = {...textStyle, ...styles.cellTextSat};
    }

    return (
      <HStack key={key} width={"auto"} height={30} 
        HAlign="|" VAlign="-"
        style={cellStyle}>
        <XText style={textStyle}>
          {str}
        </XText>
      </HStack>
    );
  }

  renderWeekBar(){
    return (
      <HStack>
        {_.map(_.range(0, 7), (o, i) => {
          return this.renderWeekBarCell(o, i);
        })}
      </HStack>
    );
  }

  render(){
    let {dayOfWeekBar} = this.props;
    return (
      <VStack>
        {dayOfWeekBar && this.renderWeekBar()}
        {this.renderMain()}
      </VStack>
    );
  }

}

export default DateCore;
